// Design system replacements for Joy components
import { useCallback } from 'react';

import { Input, Text } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions';
// Re-use shared expandable UI primitives
import { AnnotationQueueSchema, PostFeedbackBody } from '@/types/schema';

import { BaseQueueRubricForm, ItemRendererParams } from './BaseQueueRubricForm';
import {
  ExpandableComponent,
  FeedbackHeader,
  SingleFeedbackCategoryChoice,
} from './FeedbackRubricShared';

interface AlignQueueRubricFormProps {
  annotationQueue: AnnotationQueueSchema;
  runId: string;
  sessionId: string;
  feedbackMap: Record<string, PostFeedbackBody>;
  onFeedbackChange: (updated: Record<string, PostFeedbackBody>) => void;
  isLoading?: boolean;
  fewShotExplanation: string;
  onFewShotExplanationChange: (value: string) => void;
}

export function AlignQueueRubricForm(props: AlignQueueRubricFormProps) {
  const {
    annotationQueue,
    runId,
    sessionId,
    feedbackMap,
    onFeedbackChange,
    fewShotExplanation,
    onFewShotExplanationChange,
  } = props;

  const { authorize } = usePermissions();
  const canUpdate = authorize('annotation-queues:update');

  const updateFeedbackForKey = useCallback(
    (
      key: string,
      diff: { score?: number; value?: string; comment?: string } | null
    ) => {
      const newMap: Record<string, PostFeedbackBody> = { ...feedbackMap };
      if (diff) {
        newMap[key] = {
          run_id: runId,
          key,
          ...diff,
          feedback_source: { type: 'app' },
        };
      } else {
        delete newMap[key];
      }
      onFeedbackChange(newMap);
    },
    [feedbackMap, onFeedbackChange, runId]
  );

  /* ---------------------- per-item renderer ------------------------------ */
  function renderItem({
    rubricItem,
    feedback,
    index,
    onAfterSubmit,
    feedbackFormExpanded,
    setFeedbackFormExpanded,
  }: ItemRendererParams<PostFeedbackBody>) {
    const itemScores = Object.keys(rubricItem.score_descriptions ?? {});
    const body =
      itemScores.length > 0
        ? itemScores.map((score) => {
            const scoreValue = Number(score);

            return (
              <SingleFeedbackCategoryChoice
                description={rubricItem.score_descriptions?.[score]}
                categoryName={score}
                selected={feedback?.score === scoreValue}
                onSelect={() => {
                  if (!rubricItem.feedback_key) {
                    return;
                  }
                  if (feedback?.score === scoreValue) {
                    updateFeedbackForKey(rubricItem.feedback_key, null);
                    onAfterSubmit(index, true);
                  } else {
                    updateFeedbackForKey(rubricItem.feedback_key, {
                      score: scoreValue,
                    });
                    onAfterSubmit(index, false);
                  }
                }}
              />
            );
          })
        : null;

    /* ------------------ header components ----------------------------- */
    const header = rubricItem.feedback_key && (
      <FeedbackHeader
        feedbackKey={rubricItem.feedback_key}
        feedback={feedback}
        isMutating={false}
      />
    );

    const collapsedHeader = rubricItem.feedback_key && (
      <FeedbackHeader
        feedbackKey={rubricItem.feedback_key}
        feedback={feedback}
        isMutating={false}
        preview={feedback?.comment}
      />
    );

    if (!rubricItem.feedback_key) {
      return null;
    }

    return (
      <div className="flex flex-col gap-4">
        <ExpandableComponent
          key={rubricItem.feedback_key}
          expandedComponent={body}
          headerComponent={header}
          collapsedHeaderComponent={collapsedHeader}
          overrideExpanded={feedbackFormExpanded[index]}
          overrideSetExpanded={(expanded: boolean) => {
            setFeedbackFormExpanded((prev: boolean[]) => {
              const next = [...prev];
              next[index] = expanded;
              return next;
            });
          }}
          className="rounded-lg border border-secondary p-3"
        />
        <div className="flex flex-col gap-2 py-3">
          <Text variant="h3">Reasoning</Text>
          <Input
            value={fewShotExplanation}
            onChange={(e) => onFewShotExplanationChange(e)}
          />
          <Text variant="sm" className="text-tertiary">
            Used to compare your reasoning with the LLM's reasoning
          </Text>
        </div>
      </div>
    );
  }

  return (
    <BaseQueueRubricForm<PostFeedbackBody>
      annotationQueue={annotationQueue}
      runId={runId}
      sessionId={sessionId}
      feedbackMap={feedbackMap}
      isLoading={props.isLoading ?? false}
      canUpdate={canUpdate}
      itemRenderer={renderItem}
    />
  );
}
